import React, {lazy, Suspense, useEffect, useMemo, useState} from 'react';
import dayjs from "dayjs";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ArrowDropDownOutlined, ArrowDropUpOutlined, KeyboardArrowRightOutlined } from "@mui/icons-material";
import { Button, SelectDropdown } from '../';
import { NoReservationRowsOverlay } from "../NoRowsOverlay";
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import DataGridPagination from "../Pagination/DataGridPagination";
import { getAllReservations } from '../../utils/Reservation/ReservationApi';
import { setReservationConversionID } from "../../features/globalStates/globalStatesSlice";
import { RESERVATION_HISTORY_COLUMN_HEADERS, RESERVATION_YEAR, CURRENT_YEAR,NEXT_YEAR } from '../../constants';
import { useFeatureFlag } from '../../context/FeatureFlagContext';

const DataGrid = lazy(() => import('../DataGrid'));

const mapStatus = (() => {
    const statuses = {
        'DELIVERED': 'Delivered',
        'PENDING': 'Reservation Placed',
        'CANCELLED': 'Reservation Cancelled',
        'ORDERED': 'Order Placed'
    };

    return (value) => statuses[value] ?? ''
})();

const LOCALE_CURRENCY_NAME_MAP = {
    'en-US': 'USD',
    'en-DE' : 'EUR'
}

export const ReservationHistoryList = ({ userId, handleErrorMsg }) => {
    const locale = useSelector((state) => state.globalStates.locale);
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const [reservationTableData, setReservationTableData] = useState([]);
    const [filteredReservations, setFilteredReservations] = useState([]);
    const [yearFilter, setYearFilter] = useState(RESERVATION_YEAR.CURRENT_YEAR);
    const history = useHistory();
    const isReservationConversionEnabled = useFeatureFlag("IS_RESERVATIONS_CONVERSION_ENABLED");

    useEffect(() => {
        const fetchReservations = async () => {
            if (!userId) {
                handleErrorMsg('We are unable to fetch your reservations. Please refresh and try again.');
                return;
            }
    
            try {
                const data = await getAllReservations(userId);
                const tableData = data?.reservations
                    .map((item, index) => ({
                    id: index + 1,
                    modernaId: item?.reservationNumber,
                    subtotal: parseFloat(item?.reservationPricingInfo?.totalPrice),
                    poNumber: item?.purchaseOrderNumber ? item?.purchaseOrderNumber : '-',
                    datePlaced: item?.lastModifiedDate,
                    status: item?.reservationStatus,
                    reservationId: item?.reservationId
                }));
                setReservationTableData(tableData);
            } catch (error) {
                handleErrorMsg(error);
            }
        };
    
        fetchReservations();
    }, []);

    useMemo(() => {
        const currentYear = new Date().getFullYear();
        const filteredReservations = reservationTableData.filter(item => {
            const yearOfReservation = new Date(item.datePlaced).getFullYear();
            return yearFilter === RESERVATION_YEAR.CURRENT_YEAR ? yearOfReservation === currentYear : yearOfReservation < currentYear;
        });
        setFilteredReservations(filteredReservations);
    }, [reservationTableData, yearFilter]);

    const mobileColumns = [
        {
            disableColumnMenu: true,
            sortable: false,
            flex: 1,
            headerName: RESERVATION_HISTORY_COLUMN_HEADERS.RESERVATION_ID,
            field: 'modernaId',
        },
        {
            type: 'nonSelectable',
            disableColumnMenu: true,
            sortable: true,
            headerName: RESERVATION_HISTORY_COLUMN_HEADERS.RESERVATION_STATUS,
            field: 'status',
            flex: 1,
            renderCell: ({value}) => {
                return mapStatus(value);
            }
        },
        {
            field: 'null',
            headerName: '',
            disableColumnMenu: true,
            sortable: false,
            width: 32,
            renderCell: ({row}) => {
                return <Link
                    to={`/${locale}/order/${row.modernaId}`}
                    underline={'none'}
                    sx={{
                        svg: {
                            color: 'secondary.main',
                            '&:hover':{
                                color: 'primary.darker',
                            }
                        }
                    }}
                    display={'flex'}
                    variant={'link'}>
                    <KeyboardArrowRightOutlined/>
                </Link>;
            }
        }
    ]

    const desktopColumns = [
        {
            disableColumnMenu: true,
            sortable: false,
            flex: 1,
            headerName: RESERVATION_HISTORY_COLUMN_HEADERS.RESERVATION_ID,
            field: 'modernaId',
        },
        {
            type: 'nonSelectable',
            disableColumnMenu: true,
            headerName: RESERVATION_HISTORY_COLUMN_HEADERS.SUBTOTAL_AMOUNT,
            field: 'subtotal',
            renderCell: ({row}) => {
                return new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: LOCALE_CURRENCY_NAME_MAP[locale],
                }).format(row.subtotal)
            },
            flex: 1
        },

        {
            type: 'nonSelectable',
            disableColumnMenu: true,
            sortable: false,
            headerName: RESERVATION_HISTORY_COLUMN_HEADERS.PO_NUMBER,
            field: 'poNumber',
            flex: 1
        },
        {
            disableColumnMenu: true,
            headerName: RESERVATION_HISTORY_COLUMN_HEADERS.DATE_PLACED,
            field: 'datePlaced',
            flex: 1,
            renderCell: ({value}) => dayjs(value, 'YYYY-MM-DD').format('MMMM D, YYYY')
        },
        {
            type: 'nonSelectable',
            disableColumnMenu: true,
            sortable: true,
            headerName: RESERVATION_HISTORY_COLUMN_HEADERS.RESERVATION_STATUS,
            field: 'status',
            flex: 1,
            renderCell: ({value}) => {
                return mapStatus(value);
            }
        },
        //Will need this for next sprint, so not removing the code
        ...((yearFilter === RESERVATION_YEAR.CURRENT_YEAR)? [{
            type: 'nonSelectable',
            disableColumnMenu: true,
            sortable: false,
            headerName: RESERVATION_HISTORY_COLUMN_HEADERS.ACTIONS,
            field: 'action',
            flex: 1,
            renderCell: ({row}) => <ActionsComponent value={row}/>
        }] : [])
    ]

    const handleReservationConversionClick = async (reservationID) => {
        await dispatch(setReservationConversionID(reservationID));
        history.push(`/${locale}/order-form/complete-reservation`);
    }

    const ActionsComponent = ({value}) => {
        return (
            <>
                {isReservationConversionEnabled ? (value?.status === 'PENDING' && (
                    <Box>
                        <Button
                            sx={{textTransform: "none", width: '120px',
                                padding: "10px 10px",
                                "& .ButtonText": {
                                    fontSize: "14px",
                                },}}
                            data-testid='convertToOrderBtn'
                            onClick={()=> handleReservationConversionClick(value?.reservationId)}
                        >
                            Order Now
                        </Button>
                    </Box>
                )) : (
                    <Box>
                        <Link
                            to={value.status === 'CANCELLED' ? `/${locale}/summary/${value?.reservationId}` : `/${locale}/reservation/${value?.reservationId}`}
                            underline={'none'}
                            sx={{
                                color: 'primary.main',
                                fontFamily: 'Aeonik Bold',
                                '&:hover': {
                                    color: 'primary.darker',
                                }
                            }}
                            display={'flex'}
                            variant={'link'}
                        >
                            {value.status === 'CANCELLED' ? 'View' : 'View/Edit'}
                        </Link>
                    </Box>
                )}
            </>
        );
    }

    const [apiRef, setApiRef] = useState(null);

    return (
        <Box mx={{xs:'32px', lg:'100px'}} my={'60px'}>
            <Typography variant={'h2'} mb={'10px'}>Reservations</Typography>
            {
                <Box sx={{width:' 230px'}}>
                    <SelectDropdown
                        className="border"
                        menuPropsSx={{
                            borderBottomLeftRadius: 6,
                            borderBottomRightRadius: 6,
                            '& .MuiMenu-list': {
                                padding: '0px',
                            },
                            border: `1px solid gray`,
                        }}
                        menuItemStyleSx={{height: '48px'}}
                        options={[{ label: `${CURRENT_YEAR}-${NEXT_YEAR}`, value: RESERVATION_YEAR.CURRENT_YEAR},
                            { label: `2024-${CURRENT_YEAR}`, value: RESERVATION_YEAR.PAST_YEARS}]}
                        value={yearFilter}
                        onChangeHandler={(value)=> setYearFilter(value)}
                        sx={{ mb:"7px"}}
                    />
                </Box>
            }
            <Suspense>
                <DataGrid
                    onApiRef={(ref) => setApiRef(ref)}
                    getRowId={(rows) => rows.id}
                    pageSizeOptions={[10]}
                    disableRowSelectionOnClick={true}
                    sx={{
                        '.MuiDataGrid-iconButtonContainer': {
                            visibility: 'visible',
                        },
                        '.MuiDataGrid-sortIcon': {
                            opacity: 'inherit !important',
                        },
                        '.MuiDataGrid-virtualScroller': {
                            overflow: 'hidden !important',
                        },
                        '.MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: "normal",
                            lineHeight: "20px"
                        }
                    }}
                    slots={{
                        pagination: DataGridPagination,
                        noRowsOverlay: NoReservationRowsOverlay,
                        columnSortedAscendingIcon: ArrowDropUpOutlined,
                        columnSortedDescendingIcon: ArrowDropDownOutlined
                    }}
                    slotProps={{
                        noRowsOverlay: filteredReservations,
                        pagination: {
                            disabled: false
                        }
                    }}
                    getCellClassName={({field}) => ['confirmationNumber', 'date', 'poNumber', 'frieghtAmt', 'total'].includes(field) ? 'nonSelectable' : ''}
                    hideFooterSelectedRowCount={true}
                    checkboxSelection={false}
                    autoHeight={true}
                    columns={mobile ? mobileColumns : desktopColumns}
                    sortingOrder={['desc', 'asc']}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'datePlaced', sort: 'desc' }],
                        },
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    rows={filteredReservations} />
            </Suspense>
        </Box>
    );
};
